// What every surface (destination, side panel, dialog) can ask the workspace to do. Surfaces never
// navigate by themselves: they call these, and the workspace turns each into a URL change, so back
// and forward always work and a reload lands in the same place.
import { createContext, useContext } from 'react';
import type { TeamRecord, UserRecord } from '../../core/records';

export type SidePanel =
  | { kind: 'thread'; rootId: string }
  | { kind: 'channelInfo'; channelId: string; tab?: 'info' | 'members' | 'pinned' | 'files' }
  | null;

export type Dialog =
  | { kind: 'newConversation' }
  | { kind: 'browseChannels' }
  | { kind: 'createChannel' }
  | { kind: 'userProfile'; userId?: string; username?: string; anchor?: { x: number; y: number } }
  | null;

export interface Shell {
  team: TeamRecord;
  me: UserRecord;
  /** Every team the reader is in (forward / channel pickers cross teams). */
  teams: TeamRecord[];
  openChannel(channelId: string): void;
  /** Opens the thread side panel (the conversation stays put). */
  openThread(rootId: string): void;
  /** Opens the conversation ON a message: flashed, centred, with the way back to now. */
  jumpToPost(postId: string): void;
  openSidePanel(panel: SidePanel): void;
  /** The main column shows a destination (Hilos, Actividad, Guardados, Borradores). */
  openDestination(destination: 'threads' | 'activity' | 'saved' | 'drafts'): void;
  /** Message search for the team; `query` may carry modifiers (`in:`, `from:`…). */
  openSearch(query: string): void;
  openDialog(dialog: Dialog): void;
  /** A person: the profile card, anchored where they were clicked. */
  openUser(target: { userId?: string; username?: string }, anchor?: { x: number; y: number }): void;
  /** Opens (creating when needed) the DM with a person. */
  openDirectMessage(userId: string): void;
  toast(message: string, tone?: 'info' | 'error'): void;
}

export const ShellContext = createContext<Shell | null>(null);

export function useShell(): Shell {
  const s = useContext(ShellContext);
  if (!s) throw new Error('useShell outside the workspace');
  return s;
}
